import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import React from 'react';
import { CommunityBudgetUsageSupportDto } from '@api/ChallengeClient';

interface Props {
    usage: CommunityBudgetUsageSupportDto | null;
    onClose(): void;
    onCorrect(usageId: number, value: number, comment: string): Promise<void>;
}

export const CorrectUsageDialog = ({ usage, onClose, onCorrect }: Props) => {
    const [value, setValue] = React.useState('');
    const [comment, setComment] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        if (usage) {
            setValue(`-${usage.value}`);
            setComment('');
            setError(undefined);
        }
    }, [usage]);

    if (!usage) {
        return null;
    }

    const parsedValue = Number(value);
    const canSave =
        comment.trim().length > 0 &&
        value.trim().length > 0 &&
        !isNaN(parsedValue);

    const submit = async () => {
        setSaving(true);
        setError(undefined);
        try {
            await onCorrect(usage.id, parsedValue, comment.trim());
            onClose();
        } catch (e) {
            setError('Could not save the correction. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Correct usage</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <Alert variant="info">
                        <AlertDescription>
                            This appends a correction to the ledger. The
                            original entry stays, and the user&apos;s reward is
                            unaffected.
                        </AlertDescription>
                    </Alert>
                    <p className="text-sm">
                        Correcting {usage.value} {usage.currencyCode} recorded
                        on {new Date(usage.occurredAt).toLocaleDateString()} in{' '}
                        {usage.yearMonth}. The correction lands in the same
                        period.
                    </p>
                    <div className="flex flex-col gap-1.5">
                        <TextField
                            label="Correction value"
                            type="text"
                            value={value}
                            onChange={(next) => setValue(String(next))}
                        />
                        <p className="text-xs text-muted-foreground">
                            Negative reduces the recorded spend, positive
                            increases it
                        </p>
                    </div>
                    <TextField
                        label="Reason"
                        type="text"
                        required
                        multiline
                        maxRows={2}
                        value={comment}
                        onChange={(next) => setComment(String(next))}
                    />
                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        disabled={!canSave}
                        loading={saving}
                        onClick={submit}
                    >
                        Save correction
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
